import { useEffect, useState } from "@bestowinc/tinkertoy-shell-deps/react";
import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";

import type {
  LoadingPlacement,
  ModuleID,
  VersionString,
  WireLoadingModule,
} from "@bestowinc/tinkertoy-core";

import {
  FAILURE_EVENT_TAGS,
  isModuleLoadError,
  markFailureReported,
  reportFailureOnce,
  resolveErrorCode,
  resolveFailureError,
} from "../error-telemetry";
import type { LoadedModule } from "../module-loader";
import { useModuleLoader } from "../module-loader/module-loader-context";
import {
  getLoadingModuleLoadPromise,
  peekLoadedLoadingModule,
} from "../module-renderer/module-read-resource";

import { RenderFallbackBoundary } from "./render-fallback-boundary";

/** React key for a {@link LoadingModuleHost}, so a different module mounts fresh. */
export function loadingModuleKey(loadingModule: WireLoadingModule): string {
  return `${loadingModule.moduleId}@${loadingModule.version}`;
}

export type LoadingModuleHostProps = {
  loadingModule: WireLoadingModule;
  placement: LoadingPlacement;
};

type LoadState =
  | { status: "pending" }
  | { status: "ready"; definition: LoadedModule["definition"] }
  | { status: "failed" };

function stateFromCache(loadingModule: WireLoadingModule): LoadState {
  const cached = peekLoadedLoadingModule(
    loadingModule.moduleId,
    loadingModule.version,
  );
  return cached
    ? { status: "ready", definition: cached.definition }
    : { status: "pending" };
}

/**
 * Renders a flow's or view's loading module with its static props and the
 * shell-supplied placement.
 *
 * A loading state must never break the page it covers, so this renders nothing
 * while its own bundle loads and nothing (plus telemetry) if that load or the
 * loading component's render fails — it never throws. Key it with
 * {@link loadingModuleKey} so a different loading module mounts fresh.
 */
export function LoadingModuleHost({
  loadingModule,
  placement,
}: LoadingModuleHostProps): ReactNode {
  const loader = useModuleLoader();
  const [state, setState] = useState<LoadState>(() =>
    stateFromCache(loadingModule),
  );
  const { moduleId, version } = loadingModule;

  useEffect(() => {
    let cancelled = false;
    void getLoadingModuleLoadPromise(loader, moduleId, version).then(
      loaded => {
        if (!cancelled) {
          setState({ status: "ready", definition: loaded.definition });
        }
      },
      (err: unknown) => {
        if (cancelled) {
          return;
        }
        const failureError = resolveFailureError(
          err,
          `Failed to load loading module "${moduleId}" (${version}).`,
        );
        reportFailureOnce(failureError, {
          tag: FAILURE_EVENT_TAGS.loadingModuleLoad,
          errorCode: resolveErrorCode(err),
          message: failureError.message,
          error: failureError,
          moduleId,
          moduleVersion: version,
        });
        if (isModuleLoadError(err)) {
          markFailureReported(err);
        }
        setState({ status: "failed" });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [loader, moduleId, version]);

  if (state.status !== "ready") {
    return null;
  }

  const Loading = state.definition.component;
  return (
    <RenderFallbackBoundary
      fallback={null}
      onError={error => reportLoadingRenderFailure(error, moduleId, version)}
    >
      <Loading props={loadingModule.props} placement={placement} />
    </RenderFallbackBoundary>
  );
}

function reportLoadingRenderFailure(
  error: unknown,
  moduleId: ModuleID,
  version: VersionString,
): void {
  const failureError = resolveFailureError(
    error,
    `Loading module "${moduleId}" (${version}) failed to render.`,
  );
  reportFailureOnce(failureError, {
    tag: FAILURE_EVENT_TAGS.loadingModuleRender,
    errorCode: resolveErrorCode(error),
    message: failureError.message,
    error: failureError,
    moduleId,
    moduleVersion: version,
  });
}
